import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { vehicleApi } from '../../lib/api';
import DatePicker from '../../components/shared/DatePicker';

const VEHICLE_TYPES = [
  { value: '', label: '전체' },
  { value: 'sedan', label: '세단' },
  { value: 'suv', label: 'SUV' },
  { value: 'truck', label: '트럭' },
  { value: 'van', label: '밴' },
  { value: 'sports', label: '스포츠카' },
  { value: 'classic', label: '클래식카' },
  { value: 'special', label: '특수차량' },
];

const CATEGORIES = [
  { value: '', label: '전체' },
  { value: 'prop_car', label: '촬영용' },
  { value: 'rental_car', label: '렌트카' },
];

// Brands list — simple, clean
const BRANDS = [
  '롤스로이스','벤틀리','람보르기니','페라리','맥라렌','부가티','애스턴마틴','마세라티',
  '포르쉐','벤츠','BMW','아우디','레인지로버','캐딜락','렉서스','제네시스','테슬라','로터스',
  '쉐보레','포드','지프','미니','폭스바겐','현대','기아','도요타','혼다',
];

const LOCATIONS = ['', '서울', '경기', '인천', '부산', '대구', '광주', '대전'];

export default function CatalogPage() {
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    location_city: '',
    vehicle_type: '',
    category: '',
    make: '',
    pickup_date: '',
    return_date: '',
    keyword: '',
    page: 1,
  });
  const [showCategory, setShowCategory] = useState<string | null>(null);
  const [showBrands, setShowBrands] = useState(false);

  // Debug state
  const [debug, setDebug] = useState<Array<{time: string; msg: string; ok?: boolean; err?: boolean}>>([]);
  const addDebug = (msg: string, ok?: boolean, err?: boolean) => {
    setDebug(prev => [...prev.slice(-19), { time: new Date().toLocaleTimeString(), msg, ok, err }]);
  };

  const loadVehicles = useCallback(async () => {
    setLoading(true);
    const apiUrl = window.location.hostname === 'localhost' ? '/api' : 'https://purplemine.mkim0818.workers.dev/api';
    const fullUrl = `${apiUrl}/vehicles?${new URLSearchParams(filters as any).toString()}`;
    addDebug(`호출: ${fullUrl.slice(0,80)}...`);

    try {
      const res: any = await vehicleApi.search(filters);
      addDebug(`응답: ${res.meta?.total || res.vehicles?.length || 0}대`, true);
      setVehicles(res.vehicles || []);
      setMeta(res.meta || {});
    } catch (err: any) {
      addDebug(`에러: ${err.message || String(err)}`, false, true);
      console.error('Failed to load vehicles:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadVehicles(); }, [loadVehicles]);

  return (
    <div>
      {/* Hero — Abbott-style: full-width dark overlay on image/gradient, centered text */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        {/* Abstract background pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-brand-500 rounded-full blur-[120px] translate-x-1/3 -translate-y-1/3" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-accent-500 rounded-full blur-[100px] -translate-x-1/4 translate-y-1/4" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 pt-20 pb-24 md:pt-28 md:pb-32">
          <div className="max-w-3xl mx-auto text-center">
            {/* Category label — Abbott-style small bold label */}
            <p className="text-brand-300 text-sm font-semibold tracking-widest uppercase mb-4">
              Premium Vehicle Platform
            </p>

            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              촬영에 필요한<br />
              <span className="text-brand-400">모든 차량</span>을 한 곳에서
            </h1>

            <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-10 max-w-2xl mx-auto">
              영화, 드라마, 광고, 전시 촬영 현장에서 필요한 소품 차량을
              개인 소유자와 렌트카 업체로부터 중개합니다.
            </p>

            {/* Search Bar — floating card style */}
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4 md:p-5 shadow-2xl">
              <div className="flex flex-wrap gap-2.5">
                <input
                  type="text"
                  placeholder="차량명, 키워드로 검색"
                  className="flex-1 min-w-[160px] bg-white/15 border border-white/20 rounded-xl px-3 sm:px-4 py-3 text-white placeholder:text-white/50 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                  value={filters.keyword}
                  onChange={e => setFilters(f => ({ ...f, keyword: e.target.value }))}
                />
                <select
                  className="bg-white/15 border border-white/20 rounded-xl px-3 sm:px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 min-w-[100px]"
                  value={filters.location_city}
                  onChange={e => setFilters(f => ({ ...f, location_city: e.target.value }))}
                >
                  {LOCATIONS.map(l => <option key={l} value={l} className="text-gray-900">{l || '전체 지역'}</option>)}
                </select>
                <div className="w-full sm:w-36">
                  <DatePicker
                    value={filters.pickup_date}
                    onChange={d => setFilters(f => ({ ...f, pickup_date: d }))}
                    placeholder="인도일"
                  />
                </div>
                <div className="w-full sm:w-36">
                  <DatePicker
                    value={filters.return_date}
                    onChange={d => setFilters(f => ({ ...f, return_date: d }))}
                    minDate={filters.pickup_date || undefined}
                    placeholder="반납일"
                  />
                </div>
                <button onClick={loadVehicles} className="bg-brand-500 hover:bg-brand-400 text-white font-semibold px-4 sm:px-6 py-3 rounded-xl transition-colors text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-brand-500">
                  검색
                </button>
              </div>
            </div>

            {/* Stats row */}
            <div className="flex justify-center gap-8 md:gap-16 mt-8 text-sm text-slate-400">
              <div>
                <span className="block text-2xl font-bold text-white">{meta.total}+</span>
                보유 차량
              </div>
              <div className="w-px bg-white/20" />
              <div>
                <span className="block text-2xl font-bold text-white">20+</span>
                브랜드
              </div>
              <div className="w-px bg-white/20" />
              <div>
                <span className="block text-2xl font-bold text-white">전국</span>
                서비스
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="max-w-7xl mx-auto px-4 -mt-6 relative z-10">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-5 relative">
          <div className="flex items-stretch gap-3 sm:gap-4">
            {/* 차종 */}
            <div className="flex-1">
              <button onClick={() => { setShowBrands(false); setShowCategory(showCategory === 'vehicle_type' ? null : 'vehicle_type'); }}
                className={`w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-150 border focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                  filters.vehicle_type ? 'bg-slate-900 text-white border-slate-900' : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100 hover:border-gray-300'}`}>
                <span className="flex items-center gap-2 min-w-0">
                  <svg className="w-4 h-4 opacity-70 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10" /></svg>
                  <span className="truncate">{filters.vehicle_type ? VEHICLE_TYPES.find(t=>t.value===filters.vehicle_type)?.label : '차종'}</span>
                </span>
                <svg className={`w-4 h-4 transition-transform shrink-0 ${showCategory === 'vehicle_type' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </button>
              {showCategory === 'vehicle_type' && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 z-50 bg-white rounded-2xl shadow-2xl border border-gray-100 p-5 w-[calc(100vw-2rem)] sm:w-[480px]">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">차종 선택</p>
                  <div className="flex flex-wrap gap-2">
                    {VEHICLE_TYPES.map(t => (
                      <button key={t.value} onClick={() => { setFilters(f => ({ ...f, vehicle_type: t.value === filters.vehicle_type ? '' : t.value, page: 1 })); }}
                        className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                          filters.vehicle_type === t.value ? 'bg-slate-900 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}>{t.label}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* 용도 */}
            <div className="flex-1">
              <button onClick={() => { setShowBrands(false); setShowCategory(showCategory === 'category' ? null : 'category'); }}
                className={`w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-150 border focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                  filters.category ? 'bg-slate-900 text-white border-slate-900' : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100 hover:border-gray-300'}`}>
                <span className="flex items-center gap-2 min-w-0">
                  <svg className="w-4 h-4 opacity-70 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>
                  <span className="truncate">{filters.category ? CATEGORIES.find(c=>c.value===filters.category)?.label : '용도'}</span>
                </span>
                <svg className={`w-4 h-4 transition-transform shrink-0 ${showCategory === 'category' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </button>
              {showCategory === 'category' && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 z-50 bg-white rounded-2xl shadow-2xl border border-gray-100 p-5 w-[calc(100vw-2rem)] sm:w-[480px]">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">용도 선택</p>
                  <div className="flex flex-wrap gap-2">
                    {CATEGORIES.map(c => (
                      <button key={c.value} onClick={() => { setFilters(f => ({ ...f, category: c.value === filters.category ? '' : c.value, page: 1 })); }}
                        className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                          filters.category === c.value ? 'bg-slate-900 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}>{c.label}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* 브랜드 */}
            <div className="flex-1">
              <button onClick={() => { setShowCategory(null); setShowBrands(!showBrands); }}
                className={`w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-150 border focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                  filters.make ? 'bg-slate-900 text-white border-slate-900' : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100 hover:border-gray-300'}`}>
                <span className="flex items-center gap-2 min-w-0">
                  <svg className="w-4 h-4 opacity-70 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                  <span className="truncate">{filters.make ? filters.make : '브랜드'}</span>
                </span>
                <svg className={`w-4 h-4 transition-transform shrink-0 ${showBrands ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </button>
              {showBrands && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 z-50 bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 sm:p-5 w-[calc(100vw-2rem)] sm:w-[560px] max-h-[70vh] overflow-y-auto">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">브랜드 선택</p>
                  {/* Mobile: flex-wrap pills | Desktop: 5-col grid */}
                  <div className="flex flex-wrap gap-1.5 sm:grid sm:grid-cols-5 sm:gap-2">
                    <button onClick={() => { setFilters(f => ({...f, make: ''})); setShowBrands(false); }}
                      className={`whitespace-nowrap px-3 py-2 rounded-full text-xs sm:text-sm sm:rounded-xl font-medium transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                        !filters.make ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:bg-gray-100'}`}>전체</button>
                    {BRANDS.map(b => (
                      <button key={b} onClick={() => { setFilters(f => ({ ...f, make: f.make === b ? '' : b, page: 1 })); setShowBrands(false); }}
                        className={`whitespace-nowrap px-3 py-2 rounded-full text-xs sm:text-sm sm:rounded-xl font-medium transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                          filters.make === b ? 'bg-slate-900 text-white shadow-md' : 'text-slate-600 hover:bg-gray-100 hover:text-slate-900'}`}>{b}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Backdrop for closing popovers */}
        {(showCategory || showBrands) && (
          <div className="fixed inset-0 z-40" onClick={() => { setShowCategory(null); setShowBrands(false); }} />
        )}
      </section>

      {/* Vehicle Grid */}
      <section className="max-w-7xl mx-auto px-4 py-10">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600" />
          </div>
        ) : vehicles.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <p className="text-lg mb-2">검색 결과가 없습니다</p>
            <p className="text-sm">다른 조건으로 검색해보세요</p>
            <button onClick={() => window.location.reload()} className="mt-4 text-brand-600 underline text-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 rounded">새로고침</button>

            {/* DEBUG PANEL */}
            <div className="mt-8 mx-auto max-w-xl text-left">
              <details className="bg-gray-100 rounded-lg p-3">
                <summary className="text-xs font-mono text-gray-500 cursor-pointer">🔍 디버그 (클릭해서 펼치기)</summary>
                <div className="mt-2 text-[10px] font-mono space-y-0.5 max-h-60 overflow-y-auto">
                  <div className="text-gray-400">hostname: {window.location.hostname}</div>
                  <div className="text-gray-400">API_BASE: {window.location.hostname === 'localhost' ? '/api' : 'https://purplemine.mkim0818.workers.dev/api'}</div>
                  {debug.map((d, i) => (
                    <div key={i} className={d.err ? 'text-red-500' : d.ok ? 'text-green-600' : 'text-gray-500'}>
                      [{d.time}] {d.msg}
                    </div>
                  ))}
                  {debug.length === 0 && <div className="text-gray-400">(아직 로그 없음)</div>}
                </div>
              </details>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-400 mb-6">
              {meta.total}대의 차량 중 {vehicles.length}대 표시
            </p>

            {/* 3-column card grid — Abbott-style */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {vehicles.map((v: any) => {
                const photoUrl = v.photos?.[0]?.photo_url;
                const hasImage = photoUrl && !photoUrl.includes('placeholder');

                return (
                  <Link
                    key={v.id}
                    to={`/vehicles/${v.id}`}
                    className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    {/* Image — 4:3 with overlay gradient on hover */}
                    <div className="aspect-[4/3] bg-gray-100 relative overflow-hidden">
                      {hasImage ? (
                        <img
                          src={photoUrl}
                          alt={`${v.make} ${v.model}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-200">
                          <svg className="w-16 h-16 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                      {/* Category badge */}
                      <div className="absolute top-3 left-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium backdrop-blur-md ${
                          v.category === 'prop_car'
                            ? 'bg-purple-900/70 text-purple-100'
                            : 'bg-slate-900/70 text-slate-100'
                        }`}>
                          {v.category === 'prop_car' ? '촬영용' : '렌트카'}
                        </span>
                      </div>
                      {/* Vehicle type badge */}
                      <div className="absolute top-3 right-3">
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-white/80 text-gray-700 backdrop-blur-md">
                          {v.vehicle_type === 'sports' ? '스포츠' : v.vehicle_type === 'suv' ? 'SUV' : v.vehicle_type === 'sedan' ? '세단' : v.vehicle_type === 'truck' ? '트럭' : v.vehicle_type === 'van' ? '밴' : v.vehicle_type}
                        </span>
                      </div>
                    </div>

                    {/* Info */}
                    <div className="p-5">
                      <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-brand-700 transition-colors">
                        {v.make} {v.model}
                      </h3>
                      <p className="text-sm text-gray-400 mb-3">
                        {v.color || '-'} · {v.location_city}
                      </p>

                      {/* Specs row */}
                      <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                        {v.features?.split('|')[0] && (
                          <span className="truncate">{v.features.split('|')[0]}</span>
                        )}
                      </div>

                      {/* No price — contact for quote */}
                      <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                        <span className="text-sm font-medium text-brand-600">
                          렌탈 문의
                        </span>
                        <svg className="w-4 h-4 text-gray-300 group-hover:text-brand-500 group-hover:translate-x-1 transition-all" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* Pagination */}
            {meta.total_pages > 1 && (
              <div className="flex justify-center gap-2 mt-10">
                {Array.from({ length: Math.min(meta.total_pages, 10) }, (_, i) => i + 1).map(p => (
                  <button
                    key={p}
                    onClick={() => setFilters(f => ({ ...f, page: p }))}
                    className={`w-10 h-10 rounded-xl text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                      p === meta.page
                        ? 'bg-slate-900 text-white shadow-md'
                        : 'bg-white text-gray-500 border border-gray-100 hover:border-gray-300'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
// deploy-1783585860
